// pulse.js
// The ambient loop: a heartbeat that evaluates a small table of
// deterministic, slot-based triggers and posts a receipt into the chat
// ledger for every action it takes. The model is NOT in this loop — the
// Pulse is plain rules; the agent is consulted elsewhere for judgment
// calls. See docs/comprehend-live-pulse-and-demo.md.
import { liveStore } from './liveStore';
import * as recorderBridge from './recorderBridge';

const DEFAULT_INTERVAL_MS = 15 * 1000;
const STALE_HYDRATE_MS = 6 * 60 * 60 * 1000; // schedule considered stale after 6h
const QUEUE_PRESSURE_THRESHOLD = 5;
const QUEUE_PRESSURE_COOLDOWN_MS = 15 * 60 * 1000;

const fmt = (d) =>
  new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** Slots whose window contains `now`. */
const activeSlots = (schedule, now) =>
  schedule.filter((s) => new Date(s.start) <= now && now < new Date(s.end));

/** Slots that ended at or before `now`. */
const endedSlots = (schedule, now) =>
  schedule.filter((s) => new Date(s.end) <= now);

const sameSet = (a, b) =>
  a.length === b.length && a.every((x) => b.includes(x));

export class Pulse {
  /**
   * @param {Object} deps
   * @param {Object} deps.apiClient
   * @param {Function} [deps.now] - clock, injectable for tests/demo
   * @param {Function} [deps.finishNote] - async (patient) => void; called when
   *   a visit ends. Production scores + queues; the demo feeder injects
   *   canned results. Receives {conversationId, patientName}.
   * @param {number} [deps.intervalMs]
   */
  constructor(deps) {
    this.apiClient = deps.apiClient;
    this.now = deps.now || (() => new Date());
    this.finishNote = deps.finishNote || null;
    this.intervalMs = deps.intervalMs || DEFAULT_INTERVAL_MS;

    this.timer = null;
    this.lastFired = new Map(); // triggerId -> ts (cooldowns)
    this.finishedConversations = new Set(); // visitEnded runs once per visit
    this.lastRecordingTarget = []; // for auto-switch undo
    this.triggers = [
      { id: 'scheduleStale', run: (now) => this.checkScheduleStale(now) },
      { id: 'patientDue', run: (now) => this.checkPatientDue(now) },
      { id: 'visitEnded', run: (now) => this.checkVisitEnded(now) },
      { id: 'queuePressure', run: (now) => this.checkQueuePressure(now) },
      // nameDrift (transcript-based "still with Jimmy?") lands with the
      // incremental-chunk pipeline — see architecture doc Phase 4.
    ];
  }

  start() {
    if (this.timer) return;
    this.tick(); // immediate first evaluation on panel open
    this.timer = setInterval(() => this.tick(), this.intervalMs);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  async tick() {
    const now = this.now();
    for (const trigger of this.triggers) {
      try {
        // Sequential on purpose: triggers read the state earlier ones wrote.
        // eslint-disable-next-line no-await-in-loop
        await trigger.run(now);
      } catch (error) {
        console.error(`Pulse trigger ${trigger.id} failed:`, error);
      }
    }
  }

  cooldownOk(id, ms) {
    const last = this.lastFired.get(id) || 0;
    if (Date.now() - last < ms) return false;
    this.lastFired.set(id, Date.now());
    return true;
  }

  /* ===== Triggers ===== */

  async checkScheduleStale() {
    const { hydratedAt, demo } = liveStore.getState();
    if (demo) return; // demo feeder seeds its own schedule
    const stale =
      !hydratedAt ||
      Date.now() - new Date(hydratedAt).getTime() > STALE_HYDRATE_MS;
    if (!stale || !this.cooldownOk('scheduleStale', STALE_HYDRATE_MS)) return;
    await liveStore.hydrate(this.apiClient);
    const count = liveStore.getState().sessions.filter((s) => s.isToday).length;
    liveStore.addReceipt({
      text: `Schedule was stale — pulled today's visits (${count})`,
      why: 'Pulse: no sync in over 6 hours',
    });
  }

  async checkPatientDue(now) {
    const { schedule } = liveStore.getState();
    if (!schedule.length) return;
    if (!recorderBridge.recorderAvailable()) return;

    const due = activeSlots(schedule, now);
    const targetIds = [
      ...new Set(
        due
          .flatMap((s) => s.patients.map((p) => p.conversationId))
          .filter(Boolean),
      ),
    ];
    const current = recorderBridge.getRecordingIds();
    if (sameSet(targetIds, current)) return;
    if (!targetIds.length) return; // ending is handled by checkVisitEnded

    const previous = [...current];
    const names = due
      .flatMap((s) => s.patients.map((p) => p.patientName))
      .join(' + ');
    await recorderBridge.startRecording(targetIds);
    this.lastRecordingTarget = targetIds;
    liveStore.addReceipt({
      text: `Switched recording to ${names}`,
      why: `Pulse: scheduled for ${fmt(due[0].start)}`,
      onUndo: async () => {
        if (previous.length) await recorderBridge.startRecording(previous);
        else await recorderBridge.stopRecording(targetIds);
      },
    });
  }

  async checkVisitEnded(now) {
    const { schedule } = liveStore.getState();
    if (!schedule.length) return;

    const active = activeSlots(schedule, now);
    const activeIds = new Set(
      active.flatMap((s) => s.patients.map((p) => p.conversationId)),
    );

    for (const slot of endedSlots(schedule, now)) {
      for (const patient of slot.patients) {
        const { conversationId, patientName } = patient;
        if (!conversationId) continue;
        if (activeIds.has(conversationId)) continue; // back-to-back slot
        if (this.finishedConversations.has(conversationId)) continue;
        this.finishedConversations.add(conversationId);

        // Make sure the recorder is off this visit before finishing.
        const recording = recorderBridge.getRecordingIds();
        if (recording.includes(conversationId)) {
          // eslint-disable-next-line no-await-in-loop
          await recorderBridge.stopRecording([conversationId]);
        }

        liveStore.addReceipt({
          text: `${patientName}'s visit ended — finishing the note`,
          why: `Pulse: slot ended ${fmt(slot.end)}, recorder clear`,
        });
        if (this.finishNote) {
          // eslint-disable-next-line no-await-in-loop
          await this.finishNote(patient);
        }
      }
    }
  }

  checkQueuePressure(now) {
    const { schedule, queue } = liveStore.getState();
    const pending = queue.filter((q) => q.status === 'pending').length;
    if (pending < QUEUE_PRESSURE_THRESHOLD) return;
    if (schedule.length && activeSlots(schedule, now).length) return; // mid-visit
    if (!this.cooldownOk('queuePressure', QUEUE_PRESSURE_COOLDOWN_MS)) return;
    liveStore.addReceipt({
      text: `${pending} items waiting and you have a gap — want to clear the queue?`,
      why: 'Pulse: queue pressure during a schedule gap',
      action: { label: 'Start review', type: 'review' },
    });
  }
}

export default Pulse;
